/**
 * ThemeLoader
 *
 * Automatically loads the saved theme configuration from the server
 * on app startup and applies it via ThemeContext.setOrganizationTheme().
 *
 * Place this component inside both <ThemeProvider> and <SdkProvider>
 * so it has access to both contexts.
 *
 * It renders its children immediately — the theme fetch runs in the background.
 * If the user has a saved "custom" theme active on the server, it will
 * be applied as soon as the API responds.
 *
 * IMPORTANT: This loader only sets the organization theme data (colors/assets)
 * for the custom mode to use. It does NOT override the user's explicitly
 * chosen theme mode (light/dark). The theme mode is only set to 'custom'
 * when no explicit user preference exists yet (i.e., first visit).
 *
 * RACE CONDITION FIX: Waits for ThemeProvider's isInitialized flag (meaning
 * localStorage preferences have been loaded) before applying server config.
 * Without this, the server fetch could resolve before localStorage loads,
 * causing the theme to be incorrectly overwritten to 'custom'.
 */

import { useEffect, useRef, type ReactNode } from 'react';
import { useThemeConfig } from '@tonnie/sdk';
import { useTheme } from '@/core/contexts/ThemeContext';

export function ThemeLoader({ children }: { children: ReactNode }) {
  const { setOrganizationTheme, setThemeMode, themeMode, isInitialized } = useTheme();
  const { data: serverConfig } = useThemeConfig();
  const appliedRef = useRef(false);

  useEffect(() => {
    if (!isInitialized || !serverConfig || appliedRef.current) return;

    if (!serverConfig.isActive) return;

    const hasColors = serverConfig.colors && Object.keys(serverConfig.colors).length > 0;
    const hasAssets = serverConfig.assets && Object.keys(serverConfig.assets).length > 0;

    if (hasColors || hasAssets) {
      setOrganizationTheme({
        colors: serverConfig.colors,
        assets: serverConfig.assets,
      });

      // Only switch to 'custom' mode if the user hasn't explicitly chosen
      // 'light' or 'dark'. Now that we wait for isInitialized, themeMode
      // reflects the actual stored preference, not the initial default.
      if (themeMode !== 'light' && themeMode !== 'dark') {
        setThemeMode('custom');
      }

      appliedRef.current = true;
    }
  }, [isInitialized, serverConfig, setOrganizationTheme, setThemeMode, themeMode]);

  return <>{children}</>;
}
